// Shared contract with website/app/utils/cookieConsent.ts.
// The new name avoids importing legacy or host-only consent cookies.
export const COOKIE_CONSENT_COOKIE_NAME = 'digidoo_consent_v4';
export const COOKIE_CONSENT_CHANGE_KEY = 'digidoo_consent_changed';
export const COOKIE_CONSENT_VERSION = 4;
export const COOKIE_CONSENT_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

export interface ICookieConsent {
  version: typeof COOKIE_CONSENT_VERSION;
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: number;
}

export type CookieConsentCategory = 'analytics' | 'marketing';
export type ICookieConsentSelection = Pick<ICookieConsent, CookieConsentCategory>;

export interface ICookieConsentWriteOptions {
  hostname?: string;
  secure?: boolean;
}

export function createCookieConsent(selection: ICookieConsentSelection): ICookieConsent {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    analytics: selection.analytics === true,
    marketing: selection.marketing === true,
    decidedAt: Date.now(),
  };
}

export function hasCookieConsent(
  consent: ICookieConsent | null,
  categories: readonly CookieConsentCategory[],
): boolean {
  return categories.every((category) => consent?.[category] === true);
}

export function hasCookieConsentWithdrawal(previous: ICookieConsent | null, consent: ICookieConsent | null): boolean {
  return Boolean((previous?.analytics && !consent?.analytics) || (previous?.marketing && !consent?.marketing));
}

export function parseCookieConsentValue(rawValue: string | null | undefined, now = Date.now()): ICookieConsent | null {
  try {
    const consent = typeof rawValue === 'string' ? (JSON.parse(rawValue) as Partial<ICookieConsent> | null) : null;
    if (
      consent?.version !== COOKIE_CONSENT_VERSION ||
      consent.necessary !== true ||
      typeof consent.analytics !== 'boolean' ||
      typeof consent.marketing !== 'boolean' ||
      typeof consent.decidedAt !== 'number' ||
      !Number.isFinite(consent.decidedAt) ||
      consent.decidedAt > now ||
      now - consent.decidedAt >= COOKIE_CONSENT_MAX_AGE_SECONDS * 1000
    ) {
      return null;
    }
    return consent as ICookieConsent;
  } catch {
    return null;
  }
}

export function readCookieConsentFromCookieString(cookieString: string): ICookieConsent | null {
  try {
    const prefix = `${COOKIE_CONSENT_COOKIE_NAME}=`;
    const entry = cookieString
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(prefix));
    return parseCookieConsentValue(entry ? decodeURIComponent(entry.slice(prefix.length)) : null);
  } catch {
    return null;
  }
}

export function readCookieConsentFromDocument(doc: Pick<Document, 'cookie'> = document): ICookieConsent | null {
  try {
    return readCookieConsentFromCookieString(doc.cookie);
  } catch {
    return null;
  }
}

export function getCookieConsentDomain(hostname: string): string | undefined {
  const normalizedHostname = hostname.trim().toLowerCase();
  if (normalizedHostname === 'digidoo.com' || normalizedHostname.endsWith('.digidoo.com')) {
    return '.digidoo.com';
  }
  return undefined;
}

export function buildCookieConsentCookieString(
  consent: ICookieConsent,
  options: ICookieConsentWriteOptions = {},
): string {
  const segments = [
    `${COOKIE_CONSENT_COOKIE_NAME}=${encodeURIComponent(JSON.stringify(consent))}`,
    'Path=/',
    `Max-Age=${COOKIE_CONSENT_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
  ];
  const domain = options.hostname ? getCookieConsentDomain(options.hostname) : undefined;
  if (domain) segments.push(`Domain=${domain}`);
  if (options.secure) segments.push('Secure');
  return segments.join('; ');
}

export function writeCookieConsentToDocument(
  consent: ICookieConsent,
  doc: Pick<Document, 'cookie'> = document,
  options: ICookieConsentWriteOptions = {},
): void {
  const hostname = options.hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');
  const secure = options.secure ?? (typeof window !== 'undefined' && window.location.protocol === 'https:');
  try {
    doc.cookie = buildCookieConsentCookieString(consent, { hostname, secure });
  } catch {
    // Blocked cookies must not prevent using the app or choosing consent for this page.
  }
  try {
    // Notification only; the cookie is the sole source of persisted consent.
    localStorage.setItem(COOKIE_CONSENT_CHANGE_KEY, JSON.stringify(consent));
  } catch {
    // Cross-tab notifications are optional.
  }
}
